import React, { useEffect, useRef, useState } from "react";
import { observer } from "mobx-react-lite";
import { useStore } from "../stores/root-store-context";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGoogleLogin } from "@react-oauth/google";
import Layout from "../components/layout/Layout";

import {
  Button,
  Card,
  Flex,
  Form,
  FormInstance,
  FormProps,
  Input,
  message,
  Popconfirm,
  Select,
  SelectProps,
  Spin,
  Tag,
  Upload,
  Switch,
  Divider,
  ConfigProvider,
} from "antd";
import TextArea from "antd/es/input/TextArea";
import type { UploadChangeParam, UploadFile } from "antd/es/upload/interface";

import {
  DeleteOutlined,
  LinkOutlined,
  MinusCircleOutlined,
  PlusOutlined,
  SaveOutlined,
  UploadOutlined,
  GoogleOutlined,
  TeamOutlined,
  CheckCircleFilled,
  SyncOutlined,
  DisconnectOutlined,
} from "@ant-design/icons";
import FontSettings, { CompanyFonts } from "../components/elements/KanbanBoard/Font/FontSettings";
import { CompanyWebhook } from "../models/CompanyWebhook";
import { Company } from "../models/Company";

const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const jsonToArray = (json?: string): { key: string; value: string }[] => {
  if (!json) return [];
  try {
    const obj = JSON.parse(json);
    return Object.entries(obj).map(([k, v]) => ({
      key: k,
      value: String(v),
    }));
  } catch {
    return [];
  }
};

const arrayToJson = (arr: { key: string; value: string }[]) =>
  JSON.stringify(
    arr.reduce<Record<string, string>>((acc, { key, value }) => {
      if (key) acc[key] = value;
      return acc;
    }, {}),
  );

const parseFontsJson = (json?: string): CompanyFonts => {
  if (!json) return {};
  try {
    return JSON.parse(json);
  } catch {
    return {};
  }
};

const stringifyFonts = (fonts?: CompanyFonts): string => {
  if (!fonts || Object.keys(fonts).length === 0) return "";
  return JSON.stringify(fonts);
};

const CompanySettings: React.FC = observer(() => {
  const { applicationStore, countryStore, crmStore } = useStore();
  const { t, i18n } = useTranslation();
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [enableGoogleSync, setEnableGoogleSync] = useState(false);
  const [enableGoogleContactsSync, setEnableGoogleContactsSync] = useState(false);
  const [enableTeamsSync, setEnableTeamsSync] = useState(false);

  const [isConnectingGoogle, setIsConnectingGoogle] = useState<boolean>(false);
  const [isDisconnectingGoogle, setIsDisconnectingGoogle] = useState<boolean>(false);

  const navigate = useNavigate();
  const uploadRef = useRef<any>(null);

  const triggerGoogleConnect = useGoogleLogin({
    flow: 'auth-code',
    scope: 'https://www.googleapis.com/auth/contacts https://www.googleapis.com/auth/userinfo.email',
    onSuccess: async (codeResponse) => {
      setIsConnectingGoogle(true);
      try {
        const res = await crmStore.connectGoogleContacts(codeResponse.code);
        if (res && 'connected' in res && res.connected) {
          message.success(t('GoogleAccountConnected', 'Google account connected successfully!'));
          await applicationStore.updateCompany();
          setEnableGoogleContactsSync(true);
          form.setFieldValue('EnableGoogleContactsSync', true);
        } else {
          message.error(res?.error || t('FailedToConnectGoogle', 'Failed to connect Google account.'));
        }
      } catch (err: any) {
        message.error(err?.message || 'Error connecting Google account');
      } finally {
        setIsConnectingGoogle(false);
      }
    },
    onError: (error) => {
      console.error('[GoogleContacts] Google OAuth popup error:', error);
      setIsConnectingGoogle(false);
      message.error(t('GoogleLoginCancelled', 'Google login was cancelled or failed.'));
    }
  });

  const handleDisconnectGoogle = async () => {
    setIsDisconnectingGoogle(true);
    try {
      const res = await crmStore.disconnectGoogleContacts();
      if (res && 'disconnected' in res && res.disconnected) {
        message.success(t('GoogleAccountDisconnected', 'Google account disconnected successfully.'));
        await applicationStore.updateCompany();
        setEnableGoogleContactsSync(false);
        form.setFieldValue('EnableGoogleContactsSync', false);
      } else {
        message.error(res?.error || 'Failed to disconnect Google account');
      }
    } catch (err: any) {
      message.error(err?.message || 'Error disconnecting Google account');
    } finally {
      setIsDisconnectingGoogle(false);
    }
  };

  const handleSyncAllContactsNow = async () => {
    if (crmStore.isGoogleSyncing) return;
    try {
      const res = await crmStore.syncGoogleContacts();
      if (res && res.syncedCount !== undefined) {
        message.success(t('GoogleContactsSyncSuccess', `Successfully synchronized ${res.syncedCount} contacts to Google!`));
      } else {
        message.error(res?.error || t('GoogleContactsSyncFailed', 'Sync failed.'));
      }
    } catch (err: any) {
      message.error(err?.message || 'Error synchronizing contacts');
    }
  };

  const fileChange = (info: UploadChangeParam) => {
    setFileList([...info.fileList].slice(-1));
  };

  useEffect(() => {
    countryStore.GetAllAvailableCountries();
  }, [countryStore]);

  const countryOptions = React.useMemo(
    () => countryStore.toOptions(i18n?.language || "en", { withFlags: true }),
    [i18n?.language, countryStore.availableCountries.length]
  );

  useEffect(() => {
    if (!applicationStore.company) return;

    const companyForForm: Company = {
      ...applicationStore.company,
      CountryId: applicationStore.company.CountryId ?? undefined,
      Webhooks: (applicationStore.company.Webhooks ?? []).map((w) => ({
        ...w,
        headers: jsonToArray(w.headersJson),
      })) as any,
      Fonts: parseFontsJson(applicationStore.company.FontsJson),
    };

    console.log("Company for form:", companyForForm);
    form.setFieldsValue(companyForForm);

    setEnableGoogleSync(applicationStore.company.EnableGoogleSync || false);
    setEnableGoogleContactsSync(applicationStore.company.EnableGoogleContactsSync || false);
    setEnableTeamsSync(applicationStore.company.EnableTeamsSync || false);

    if (applicationStore.company.LogoId) {
      setFileList([
        {
          uid: "-1",
          name: applicationStore.company.LogoId,
          status: "done",
          url: `${applicationStore?.siteConfig?.baseCloudinary}/image/upload/${applicationStore.company.LogoId}`,
        },
      ]);
    }
  }, [applicationStore.company, form]);

  const onFinish: FormProps<any>["onFinish"] = async (values: any) => {
    message.loading({ content: t("Saving...") || "Saving...", key: "companySave" });

    if (fileList.length && fileList[0].uid !== "-1") {
      await applicationStore.uploadPhoto(
        fileList[0],
        applicationStore.company?.Id || "",
      );
    }

    const payload: Company = {
      ...values,
      Id: values.Id || applicationStore.company?.Id,
      Tenant: values.Tenant || applicationStore.company?.Tenant || "",
      EnableGoogleSync: enableGoogleSync,
      EnableGoogleContactsSync: enableGoogleContactsSync,
      EnableTeamsSync: enableTeamsSync,
      GoogleContactsRefreshToken: applicationStore.company?.GoogleContactsRefreshToken,
      GoogleContactsConnectedEmail: applicationStore.company?.GoogleContactsConnectedEmail,
      Webhooks: (values.Webhooks ?? []).map((w: any) => {
        const { headers = [], ...rest } = w;
        return {
          ...rest,
          headersJson: arrayToJson(headers),
        } as CompanyWebhook;
      }),
      FontsJson: stringifyFonts(values.Fonts),
    };

    console.log("Payload to save:", payload);

    try {
      const res = await applicationStore.editCompany(payload);
      if (res !== false) {
        message.success({ content: t("Successfully saved!") || "Successfully saved!", key: "companySave", duration: 3 });
        await applicationStore.updateCompany();
      } else {
        message.error({ content: t("FailedToSaveSettings") || "Failed to save settings. Please check your data.", key: "companySave", duration: 4 });
      }
    } catch (err: any) {
      message.error({ content: err?.message || "An error occurred while saving.", key: "companySave", duration: 4 });
    }
  };

  const onFinishFailed: FormProps<any>["onFinishFailed"] = (errorInfo) => {
    console.warn("Form validation failed:", errorInfo);
    const firstErr = errorInfo?.errorFields?.[0]?.errors?.[0];
    const fieldName = errorInfo?.errorFields?.[0]?.name?.join?.(".") || "Field";
    message.error({
      content: `${t("Validation error") || "Validation error"}: ${fieldName} - ${firstErr || "required field"}`,
      key: "companySave",
      duration: 5,
    });
  };

  const optionsDays: SelectProps["options"] = [
    { value: 0, label: "Sunday" },
    { value: 1, label: "Monday" },
    { value: 2, label: "Tuesday" },
    { value: 3, label: "Wednesday" },
    { value: 4, label: "Thursday" },
    { value: 5, label: "Friday" },
    { value: 6, label: "Saturday" },
  ];

  const tagRender: SelectProps["tagRender"] = (props) => {
    const { label, closable, onClose } = props;
    return (
      <Tag color="error" closable={closable} onClose={onClose}>
        {label}
      </Tag>
    );
  };

  const eventOptions = [
    { value: "PreUserAdd", label: t("Pre-add user web-hook URL") },
    { value: "PostUserAdd", label: t("Post-add user web-hook URL") },
    { value: "CrmContactCreated", label: "CRM: Contact Created" },
    { value: "CrmContactUpdated", label: "CRM: Contact Updated" },
    { value: "GoogleContactsSync", label: "CRM: Google Contacts Sync Triggered" },
  ];

  const timezoneOptions = [
    { value: "UTC", label: "UTC" },
    { value: "Eastern Standard Time", label: "Eastern Time (US & Canada)" },
    { value: "Central Standard Time", label: "Central Time (US & Canada)" },
    { value: "Mountain Standard Time", label: "Mountain Time (US & Canada)" },
    { value: "Pacific Standard Time", label: "Pacific Time (US & Canada)" },
    { value: "GMT Standard Time", label: "London (GMT)" },
    { value: "Central European Standard Time", label: "Central European Time" },
    { value: "E. Europe Standard Time", label: "Eastern European Time" },
  ];

  const testWebhook = async (
    formRef: FormInstance,
    idx: number,
    httpMethod: "Get" | "Post",
  ) => {
    const current = formRef.getFieldValue(["Webhooks", idx]);
    const method = httpMethod === "Get" ? "GET" : "HEAD";

    try {
      await fetch(current.url, { method, mode: "no-cors" });
      message.success("Request reached the server (opaque response)");
    } catch (e: any) {
      message.error(e.message ?? "Network error");
    }
  };

  return (
    <Layout headerStyle={1} footerStyle={2}>
      {contextHolder}
      <div style={{ maxWidth: 750, margin: "20px auto 100px" }}>
        <Button onClick={() => navigate(-1)}>{t("Back")}</Button>

        <Spin spinning={applicationStore.isLoading}>
          <Form
            form={form}
            onFinish={onFinish}
            onFinishFailed={onFinishFailed}
            scrollToFirstError
            layout="vertical"
          >
            <Form.Item>
              <Flex vertical align="center">
                <Upload
                  ref={uploadRef}
                  accept="image/*"
                  maxCount={1}
                  listType="picture-card"
                  fileList={fileList}
                  onChange={fileChange}
                  beforeUpload={(file) => {
                    const isLt10Mb = file.size <= MAX_FILE_SIZE;

                    if (!isLt10Mb) {
                      message.error(t("fileSizeError"));
                      return Upload.LIST_IGNORE;
                    }

                    return false;
                  }}
                  showUploadList={{
                    showPreviewIcon: true,
                    showRemoveIcon: true,
                  }}
                >
                  {fileList.length === 0 && <UploadOutlined />}
                </Upload>
                {fileList.length > 0 && (
                  <Button
                    icon={<UploadOutlined />}
                    onClick={() =>
                      (document.querySelector(
                        'input[type="file"]',
                      ) as HTMLElement).click()
                    }
                  >
                    {t("Upload")}
                  </Button>
                )}
              </Flex>
            </Form.Item>

            <Form.Item name="Id">
              <Input hidden />
            </Form.Item>

            <Form.Item
              label={t("CompanyName")}
              name="Title"
              rules={[{ required: true, message: t("CompanyNameRequired") || "Please enter company name" }]}
            >
              <Input placeholder={t("CompanyName")} />
            </Form.Item>

            <Form.Item
              label={t("Tenant")}
              name="Tenant"
            >
              <Input disabled placeholder={t("Tenant")} />
            </Form.Item>

            <Form.Item
              label={t("CompanyDescription")}
              name="Description"
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item
              label={t("Country")}
              name="CountryId">
              <Select
                showSearch
                placeholder={t("Country")}
                options={countryOptions}
                optionFilterProp="name"
                optionLabelProp="name"
                filterOption={(input, option) =>
                  (option as any)?.name?.toLowerCase().includes(input.toLowerCase())
                }
              />
            </Form.Item>

            <Form.Item
              label={t("Address")}
              name="Adress"
            >
              <TextArea rows={4} />
            </Form.Item>

            <Form.Item
              hidden={!applicationStore.siteConfig.isShowLanguage}
              label={t("CompanyNotificationLanguage")}
              name="NotificationLanguage"
            >
              <Select
                options={[
                  { value: "en", label: t("EnLang") },
                  { value: "ru", label: t("RuLang") },
                  { value: "uk", label: t("UkLang") },
                ]}
              />
            </Form.Item>

            <Form.Item
              label={t("Currency")}
              name="Currency"
            >
              <Select
                disabled={!applicationStore.siteConfig.isShowLanguage}
                options={[
                  { value: "$", label: "$ USD" },
                  { value: "€", label: "€ EUR" },
                  { value: "₴", label: "₴ UAH" },
                  { value: "£", label: "£ GBP" },
                ]}
              />
            </Form.Item>

            <Form.Item label={t("WeekendDays")} name="WeekendDays">
              <Select mode="multiple" tagRender={tagRender} options={optionsDays} />
            </Form.Item>

            <Form.Item
              label={t("vacationWorkHours")}
              name="HoursVacation"
            >
              <Input type="number" max={24} />
            </Form.Item>

            <Form.Item
              label={t("sickLeaveWorkHours")}
              name="HoursSickLeave"
            >
              <Input type="number" max={24} />
            </Form.Item>

            <Divider>Integrations & Synchronization</Divider>

            <Card
              size="small"
              title={
                <Flex align="center" gap={8}>
                  <GoogleOutlined style={{ fontSize: 20, color: "#4285F4" }} />
                  <span>Google Workspace & CRM Integration</span>
                </Flex>
              }
              style={{ marginBottom: 16 }}
            >
              {/* ─────────── Google Contacts (OAuth 2.0) ─────────── */}
              <Form.Item
                label={
                  <Flex align="center" gap={6}>
                    <span style={{ fontWeight: 600 }}>Enable Google Contacts Sync (CRM)</span>
                    <Tag color="blue" style={{ borderRadius: 4 }}>OAuth 2.0</Tag>
                  </Flex>
                }
                name="EnableGoogleContactsSync"
                valuePropName="checked"
              >
                <Switch onChange={setEnableGoogleContactsSync} />
              </Form.Item>

              {enableGoogleContactsSync && (
                <div
                  style={{
                    background: "#f8fafc",
                    border: "1px solid #e2e8f0",
                    borderRadius: 10,
                    padding: "16px 20px",
                    marginBottom: 20,
                  }}
                >
                  {applicationStore.company?.GoogleContactsConnectedEmail ? (
                    <Flex vertical gap={12}>
                      <Flex align="center" justify="space-between" wrap="wrap" gap={12}>
                        <Flex align="center" gap={12}>
                          <div
                            style={{
                              width: 40,
                              height: 40,
                              borderRadius: "50%",
                              background: "#e8f0fe",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              color: "#1a73e8",
                              fontSize: 20,
                            }}
                          >
                            <GoogleOutlined />
                          </div>
                          <div>
                            <div style={{ fontSize: 12, color: "#64748b" }}>Connected Google Account</div>
                            <div style={{ fontSize: 15, fontWeight: 600, color: "#0f172a" }}>
                              {applicationStore.company.GoogleContactsConnectedEmail}
                            </div>
                          </div>
                        </Flex>
                        <Tag
                          color="success"
                          icon={<CheckCircleFilled />}
                          style={{ padding: "4px 12px", fontSize: 13, borderRadius: 6, display: "flex", alignItems: "center", gap: 4 }}
                        >
                          Auto-Sync Active
                        </Tag>
                      </Flex>

                      <Divider style={{ margin: "8px 0" }} />

                      <Flex align="center" justify="space-between" wrap="wrap" gap={10}>
                        <Button
                          type="primary"
                          ghost
                          icon={<SyncOutlined spin={crmStore.isGoogleSyncing} />}
                          loading={crmStore.isGoogleSyncing}
                          disabled={crmStore.isGoogleSyncing}
                          onClick={handleSyncAllContactsNow}
                        >
                          Sync All Contacts Now
                        </Button>

                        <Popconfirm
                          title="Disconnect Google Account"
                          description="Are you sure you want to disconnect this Google account? New contacts will no longer be synced."
                          okText="Yes, disconnect"
                          cancelText="Cancel"
                          okButtonProps={{ danger: true }}
                          onConfirm={handleDisconnectGoogle}
                        >
                          <Button danger loading={isDisconnectingGoogle} icon={<DisconnectOutlined />}>
                            Disconnect Account
                          </Button>
                        </Popconfirm>
                      </Flex>
                    </Flex>
                  ) : (
                    <Flex vertical gap={12}>
                      <div style={{ color: "#475569", fontSize: 14, lineHeight: 1.5 }}>
                        Connect your Google account once. Contacts created or updated in CRM will be automatically synchronized with your Google Contacts.
                      </div>
                      <div>
                        <Button
                          type="primary"
                          size="middle"
                          icon={<GoogleOutlined />}
                          loading={isConnectingGoogle}
                          onClick={() => triggerGoogleConnect()}
                          style={{
                            background: "#4285F4",
                            borderColor: "#4285F4",
                            fontWeight: 500,
                            borderRadius: 6,
                          }}
                        >
                          Connect Google Account
                        </Button>
                      </div>
                    </Flex>
                  )}
                </div>
              )}

              <Divider style={{ margin: "16px 0" }} />

              {/* ─────────── Google Calendar (Service Account) ─────────── */}
              <Form.Item
                label="Enable Google Calendar Sync"
                name="EnableGoogleSync"
                valuePropName="checked"
              >
                <Switch onChange={setEnableGoogleSync} />
              </Form.Item>

              {enableGoogleSync && (
                <>
                  <Form.Item
                    label="Google Email Domain / User Impersonation"
                    name="GoogleEmailDomain"
                    rules={[{ required: enableGoogleSync }]}
                    tooltip="Domain or specific Google account email used for Google Workspace delegation"
                  >
                    <Input placeholder="example.com or admin@company.com" />
                  </Form.Item>

                  <Form.Item
                    label="Google Application Name"
                    name="GoogleApplicationName"
                    rules={[{ required: enableGoogleSync }]}
                  >
                    <Input placeholder="My Company App" />
                  </Form.Item>

                  <Form.Item
                    label="Google Calendar ID"
                    name="GoogleCalendarId"
                    rules={[{ required: enableGoogleSync }]}
                  >
                    <Input placeholder="primary or calendar@group.calendar.google.com" />
                  </Form.Item>

                  <Form.Item
                    label="Google Service Account Credentials (JSON)"
                    name="GoogleCredentialsJson"
                    rules={[{ required: enableGoogleSync }]}
                    tooltip="Google Cloud Service Account JSON Key with Domain-Wide Delegation (Calendar API enabled)"
                  >
                    <TextArea
                      rows={6}
                      placeholder='{"type": "service_account", "project_id": "...", ...}'
                    />
                  </Form.Item>
                </>
              )}
            </Card>

            <Card
              size="small"
              title={
                <Flex align="center" gap={8}>
                  <TeamOutlined style={{ fontSize: 20, color: "#5B5FC7" }} />
                  <span>Microsoft Teams/Graph Integration</span>
                </Flex>
              }
              style={{ marginBottom: 16 }}
            >
              <Form.Item
                label="Enable Microsoft Teams Sync"
                name="EnableTeamsSync"
                valuePropName="checked"
              >
                <Switch onChange={setEnableTeamsSync} />
              </Form.Item>

              {enableTeamsSync && (
                <>
                  <Form.Item
                    label="Teams Email Domain"
                    name="TeamsEmailDomain"
                    rules={[{ required: enableTeamsSync }]}
                  >
                    <Input placeholder="example.com" />
                  </Form.Item>

                  <Form.Item
                    label="Azure AD Tenant ID"
                    name="GraphTenantId"
                    rules={[{ required: enableTeamsSync }]}
                  >
                    <Input placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                  </Form.Item>

                  <Form.Item
                    label="Azure AD Client ID"
                    name="GraphClientId"
                    rules={[{ required: enableTeamsSync }]}
                  >
                    <Input placeholder="xxxxxxxx-xxxx-xxxx-xxxx-xxxxxxxxxxxx" />
                  </Form.Item>

                  <Form.Item
                    label="Azure AD Client Secret"
                    name="GraphClientSecret"
                    rules={[{ required: enableTeamsSync }]}
                  >
                    <Input.Password placeholder="Client secret value" />
                  </Form.Item>

                  <Form.Item
                    label="Graph Calendar ID"
                    name="GraphCalendarId"
                    rules={[{ required: enableTeamsSync }]}
                  >
                    <Input placeholder="Calendar ID or email address" />
                  </Form.Item>

                  <Form.Item
                    label="Graph Time Zone"
                    name="GraphTimeZone"
                    rules={[{ required: enableTeamsSync }]}
                  >
                    <Select options={timezoneOptions} placeholder="Select timezone" />
                  </Form.Item>
                </>
              )}
            </Card>

            <Divider>Font Settings</Divider>
            <Form.Item name="Fonts">
              <FontSettings />
            </Form.Item>

            <Divider>Webhooks</Divider>
            <Form.List name="Webhooks">
              {(fields, { add, remove }) => (
                <>
                  {fields.map(({ key, name, ...rest }) => (
                    <Card key={key} size="small" style={{ marginBottom: 14 }}>
                      <Flex gap={8} align="flex-start" wrap="wrap">
                        <Form.Item
                          {...rest}
                          name={[name, "eventType"]}
                          label={t("Event")}
                          rules={[{ required: true }]}
                          style={{ width: 180 }}
                        >
                          <Select options={eventOptions} />
                        </Form.Item>

                        <Form.Item
                          {...rest}
                          name={[name, "httpMethod"]}
                          label={t("Method")}
                          rules={[{ required: true }]}
                          style={{ width: 120 }}
                        >
                          <Select
                            options={[
                              { value: "Post", label: "POST" },
                              { value: "Get", label: "GET" },
                            ]}
                          />
                        </Form.Item>

                        <Form.Item
                          {...rest}
                          name={[name, "url"]}
                          label="URL"
                          style={{ flex: 1, minWidth: 250 }}
                          rules={[
                            { required: true, message: t("Enter URL") },
                            { type: "url", message: t("URL is invalid") },
                          ]}
                        >
                          <Input placeholder="https://example.com/hook" />
                        </Form.Item>
                      </Flex>

                      <Form.List name={[name, "headers"]}>
                        {(hdrFields, hdrOps) => (
                          <>
                            {hdrFields.map(({ key: hk, name: hn, ...hr }) => (
                              <Flex
                                key={hk}
                                gap={8}
                                align="baseline"
                                style={{ marginBottom: 6 }}
                              >
                                <Form.Item
                                  {...hr}
                                  name={[hn, "key"]}
                                  style={{ flex: 1 }}
                                  rules={[{ required: true }]}
                                >
                                  <Input placeholder="Header name" />
                                </Form.Item>
                                <Form.Item
                                  {...hr}
                                  name={[hn, "value"]}
                                  style={{ flex: 2 }}
                                  rules={[{ required: true }]}
                                >
                                  <Input placeholder="Header value" />
                                </Form.Item>
                                <MinusCircleOutlined
                                  onClick={() => hdrOps.remove(hn)}
                                />
                              </Flex>
                            ))}

                            <Form.Item>
                              <Button
                                type="dashed"
                                icon={<PlusOutlined />}
                                block
                                onClick={() => hdrOps.add({ key: "", value: "" })}
                              >
                                {t("Add header")}
                              </Button>
                            </Form.Item>
                          </>
                        )}
                      </Form.List>

                      <Flex justify="space-between">
                        <Button
                          icon={<LinkOutlined />}
                          onClick={() =>
                            testWebhook(
                              form,
                              name,
                              form.getFieldValue(["Webhooks", name, "httpMethod"]),
                            )
                          }
                        >
                          {t("Test")}
                        </Button>
                        <Button
                          danger
                          icon={<DeleteOutlined />}
                          onClick={() => remove(name)}
                        />
                      </Flex>
                    </Card>
                  ))}

                  <Form.Item>
                    <Button
                      type="dashed"
                      block
                      icon={<PlusOutlined />}
                      onClick={() =>
                        add({
                          eventType: "PreUserAdd",
                          httpMethod: "Post",
                          url: "",
                          headers: [{ key: "", value: "" }],
                        })
                      }
                    >
                      {t("Add web-hook")}
                    </Button>
                  </Form.Item>
                </>
              )}
            </Form.List>

            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                icon={<SaveOutlined />}
                style={{ height: 48, float: "right" }}
              >
                {t("Save")}
              </Button>
            </Form.Item>
          </Form>
        </Spin>
      </div>
    </Layout>
  );
});

export default CompanySettings;